/**
 * gateBanner — what the tab says about the member's stake lifecycle (ARC-45).
 *
 * Pure. No React, no I/O. One input (the `gate` lifecycle and
 * `openPositionRiding`, both straight off `/status.staking`) and one output:
 * which banner to render, if any.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * THE LIFECYCLE IS READ, NEVER DERIVED — the same rule slotGate.js holds for
 * counts, for the same reason. Nothing here compares `slotsUsed` to
 * `slotsTotal`. Only the engine knows whether a position is riding, and that is
 * the entire difference between "closing_only" and "blocked":
 *
 *   active        the stake covers the allocations. No banner at all.
 *   closing_only  the stake lapsed WITH a position open. Per Gven's rule the
 *                 open trade runs to its natural close; only new opens stop.
 *   blocked       the stake lapsed with nothing riding. New trades stop.
 *
 * A client that recomputed this from counts would produce a second opinion that
 * disagrees with the engine exactly when it matters most — and the failure mode
 * is the cruel one: telling a member their live trade is "paused" when the
 * engine is still managing it to its exit.
 *
 * NULL/UNKNOWN GATE ⇒ NO BANNER. An unread lifecycle is not a lapsed one. The
 * fail-closed response to "we couldn't read your stake" is to LOCK the rows —
 * slotGate.js already does that — not to announce a lapse that may not exist.
 *
 * COMPLIANCE: every string below describes account state and product mechanics.
 * No return, price, projection, promise or advice appears in this file.
 */

/** The gate lifecycle values the engine serves (ARC-42). */
export const GATE = {
  ACTIVE: "active",
  CLOSING_ONLY: "closing_only",
  BLOCKED: "blocked",
};

/* ── the exact copy ──────────────────────────────────────────────────────────
   Both strings are specified verbatim in ARC-45 (the Aug 11 spec for the first
   one). They are exported so the tests assert the constant rather than a
   retyped copy of it — a test that retypes the sentence cannot catch a drift in
   the sentence. Do not reword either without changing the spec first. */

export const GATE_CLOSING_ONLY_COPY =
  "Stake lapsed — current trade will complete; new trades paused.";

export const GATE_BLOCKED_COPY =
  "Stake lapsed — new trades paused. Re-stake a Supernova to resume.";

/* The line that stops "paused" from reading as "abandoned". A member with a
   live position who sees the word paused must be told, in the same breath, that
   the engine is still managing that position through to its exit — this is the
   fail-OPEN-on-close half of the rule (ARC-43 enforces it at signal time). */
export const OPEN_POSITION_RIDING_COPY =
  "Your open position is still being managed through to its close — only new trades are paused.";

/**
 * @param {Object} input
 * @param {string|null} input.gate  the engine's lifecycle, verbatim. Anything
 *        that is not a known non-active value ⇒ null (no banner).
 * @param {boolean} [input.openPositionRiding]  the engine's answer, never ours.
 * @returns {{gate: string, headline: string, riding: boolean}|null}
 */
export function deriveGateBanner(input = {}) {
  const { gate = null, openPositionRiding = false } = input;

  if (gate === GATE.CLOSING_ONLY) {
    return {
      gate,
      headline: GATE_CLOSING_ONLY_COPY,
      riding: Boolean(openPositionRiding),
    };
  }

  if (gate === GATE.BLOCKED) {
    return {
      gate,
      headline: GATE_BLOCKED_COPY,
      /* "blocked" means nothing is riding — but the flag is still the engine's
         to set, and if it ever says otherwise we say so rather than overrule
         it. Reading beats deriving here too. */
      riding: Boolean(openPositionRiding),
    };
  }

  /* active, null, or a lifecycle this build does not know. No banner. */
  return null;
}

export default deriveGateBanner;
